import { useEffect, useState } from "react";

let ringId = 0;

// Big rings: each gets its own trajectory, speed and wobble — nobody blows three identical O's.
const RING_PRESETS = [
  { dx: "-26px", rise: "-155px", rot: "-4deg", dur: "5.1s" },
  { dx: "14px", rise: "-195px", rot: "3deg", dur: "4.6s" },
  { dx: "-8px", rise: "-175px", rot: "-2deg", dur: "5.4s" },
];
const STAGGER_MS = 450;
const BIG_LIFE_MS = 8000;
const MINI_LIFE_MS = 3400;
const MAX_RINGS = 40;

const rand = (min, max) => min + Math.random() * (max - min);

const miniRing = (x, y) => ({
  id: ++ringId,
  x,
  y,
  mini: true,
  delay: 0,
  dx: `${rand(-30, 30).toFixed(0)}px`,
  rise: `${rand(-90, -50).toFixed(0)}px`,
  rot: `${rand(-8, 8).toFixed(0)}deg`,
  dur: `${rand(2.4, 3.2).toFixed(2)}s`,
  born: Date.now(),
});

export default function SmokeRings() {
  const [rings, setRings] = useState([]);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let taps = [];
    let lastTrail = 0;
    let lastPos = null;

    const spawn = (batch) => setRings((r) => [...r.slice(-MAX_RINGS), ...batch]);

    const onDown = (e) => {
      const interactive = e.target.closest(
        "a, button, input, textarea, select, form, [data-secret-trigger]"
      );
      if (interactive) {
        // pressing a button/link = one quick puff
        spawn([miniRing(e.clientX, e.clientY)]);
        return;
      }
      const now = Date.now();
      taps = taps.filter((t) => now - t < 1200);
      taps.push(now);
      if (taps.length >= 3) {
        taps = [];
        const now2 = Date.now();
        spawn(
          RING_PRESETS.map((preset, i) => ({
            id: ++ringId,
            x: e.clientX,
            y: e.clientY,
            mini: false,
            delay: i * STAGGER_MS,
            born: now2,
            ...preset,
          }))
        );
      }
    };

    // finger held + dragging (scroll) = trail of O's following the finger
    const onMove = (e) => {
      if (e.pointerType !== "touch" && !(e.buttons > 0)) return;
      const now = Date.now();
      const moved = !lastPos || Math.hypot(e.clientX - lastPos.x, e.clientY - lastPos.y) > 28;
      if (now - lastTrail > 90 && moved) {
        lastTrail = now;
        lastPos = { x: e.clientX, y: e.clientY };
        spawn([miniRing(e.clientX, e.clientY)]);
      }
    };

    const cleaner = setInterval(() => {
      const now = Date.now();
      setRings((r) =>
        r.filter((ring) => now - ring.born < (ring.mini ? MINI_LIFE_MS : BIG_LIFE_MS))
      );
    }, 1500);

    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      clearInterval(cleaner);
    };
  }, []);

  return (
    <div data-testid="smoke-rings-layer" className="pointer-events-none fixed inset-0 z-[90]" aria-hidden="true">
      {rings.map((r) => {
        const size = r.mini ? 112 : 224;
        const style = {
          left: r.x - size / 2,
          top: r.y - size / 2,
          "--delay": `${r.delay}ms`,
          "--dx": r.dx,
          "--rise": r.rise,
          "--rot": r.rot,
          "--dur": r.dur,
        };
        if (r.mini) {
          return (
            <img
              key={r.id}
              src="/images/smoke-ring.png"
              alt=""
              className="smoke-ring-mini absolute w-28 max-w-none"
              style={style}
            />
          );
        }
        return (
          <span key={r.id}>
            <img src="/images/smoke-ring.png" alt="" className="smoke-haze absolute w-56 max-w-none" style={style} />
            <img src="/images/smoke-ring.png" alt="" className="smoke-ring absolute w-56 max-w-none" style={style} />
          </span>
        );
      })}
    </div>
  );
}
